import { EyeOutlined } from "@ant-design/icons";
import {
  Button,
  Drawer,
  Dropdown,
  Form,
  Input,
  Select,
  Space,
  Switch,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../app/store";
import ActionThreeDotIcon from "../../../../assets/icons/ActionThreeDotIcon";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import FilterIconThree from "../../../../assets/icons/FilterIconThree";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SuccessModal from "../../../../components/common/SuccessModal";
import CustomTable from "../../../../components/ui/CustomTable";
import IconButton from "../../../../components/ui/IconButton";
import Loader from "../../../../components/ui/Loader";
import {
  useCreateCollectionPointMutation,
  useGetAllCollectionPointQuery,
  useGetSingleCollectionPointQuery,
  useUpdateCollectionPointMutation,
  useUpdateCollectionPointStatusMutation,
} from "../../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import {
  useGetAllAreaQuery,
  useGetAllRouteQuery,
  useGetAllTerritoryQuery,
  useGetAllZoneQuery,
} from "../../../../features/cashCollection/geoSetup/geoSetupApi";
import { useGetAllUsersQuery } from "../../../../features/portal/user/userApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import ConfirmModalLayout from "../../../../layouts/ConfirmModalLayout";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import {
  GetAllCollectionPointDataParamsType,
  GetAllCollectionPointDataType,
} from "../../../../types/cashCollection/cashCollectionSetup";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import CashCollectionDetailsModal from "../../FieldOfficers/CashCollectionDetailsModal";

export default function IndividualPointList() {
  const [open, setOpen] = useState(false);
  const [openOfficer, setOpenOfficer] = useState(false);
  const [form] = Form.useForm();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllCollectionPointDataParamsType>();

  const {
    open: openViewModal,
    handleOpen: handleViewOpen,
    handleClose: handleViewClose,
  } = useModal<any, "VIEW">();

  const {
    open: openConfirmModal,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose,
  } = useModal<any, "CONFIRM">();

  const {
    open: openConfirmStatusModal,
    handleOpen: handleConfirmStatusOpen,
    handleClose: handleConfirmStatusClose,
  } = useModal<any, "CONFIRMSTATUS">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();

  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(
    filter?.zoneId ?? null
  );
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(
    filter?.areaId ?? null
  );
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<number | null>(
    filter?.territoryId ?? null
  );
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(
    filter?.routeId ?? null
  );
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    filter?.userId ?? null
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { user } = useAppSelector((state) => state.auth);

  const { isError, error, isLoading, data } = useGetAllCollectionPointQuery({
    params: {
      ...filter,
      search: keyword,
      skip: undefined,
      page,
      limit,
      collectionPointType: "individual",
    },
  });

  const [
    createCollectionPoint,
    {
      isError: createCollectionPointIsError,
      error: createCollectionPointError,
      isLoading: createCollectionPointIsLoading,
      isSuccess: createCollectionPointIsSuccess,
    },
  ] = useCreateCollectionPointMutation();
  const [
    updateCollectionPoint,
    {
      isError: updateCollectionPointIsError,
      error: updateCollectionPointError,
      isSuccess: updateCollectionPointIsSuccess,
      isLoading: updateCollectionPointIsLoading,
    },
  ] = useUpdateCollectionPointMutation();

  const {
    isError: getSingleCollectionPointIsError,
    error: getSingleCollectionPointError,
    isLoading: getSingleCollectionPointIsLoading,
    data: getSingleCollectionPointData,
    isFetching: getSingleCollectionPointIsFetching,
  } = useGetSingleCollectionPointQuery(selectedId, {
    skip: !selectedId,
  });

  const [
    updateCollectionPointStatus,
    {
      isError: updateCollectionPointStatusIsError,
      error: updateCollectionPointStatusError,
      isSuccess: updateCollectionPointStatusIsSuccess,
      isLoading: updateCollectionPointStatusIsLoading,
    },
  ] = useUpdateCollectionPointStatusMutation();

  const {
    isError: getAllUserIsError,
    error: getAllUserError,
    isLoading: getAllUserIsLoading,
    data: getAllUserData,
  } = useGetAllUsersQuery(
    {
      params: {
        company_id: user?.data?.company_id,
        page: 1,
        limit: 100000000,
      },
    },
    {
      skip: !user?.data?.company_id,
    }
  );

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
    isLoading: getAllZoneIsLoading,
    isFetching: getAllZoneIsFetching,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  // Get all Areas
  const {
    isError: getAllAreaIsError,
    error: getAllAreaError,
    data: getAllAreaData,
    isLoading: getAllAreaIsLoading,
    isFetching: getAllAreaIsFetching,
  } = useGetAllAreaQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        zoneId: selectedZoneId ? selectedZoneId : undefined,
      },
    },
    {
      skip: !selectedZoneId,
    }
  );

  // Get all Territories
  const {
    isError: getAllTerritoryIsError,
    error: getAllTerritoryError,
    data: getAllTerritoryData,
    isLoading: getAllTerritoryIsLoading,
    isFetching: getAllTerritoryIsFetching,
  } = useGetAllTerritoryQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        areaId: selectedAreaId ? selectedAreaId : undefined,
      },
    },
    {
      skip: !selectedAreaId,
    }
  );

  // Get all Routes
  const {
    isError: getAllRouteIsError,
    error: getAllRouteError,
    data: getAllRouteData,
    isLoading: getAllRouteIsLoading,
    isFetching: getAllRouteIsFetching,
  } = useGetAllRouteQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        territoryId: selectedTerritoryId ? selectedTerritoryId : undefined,
      },
    },
    {
      skip: !selectedTerritoryId,
    }
  );

  useError(isError, error, "Get all collection point data fail");
  useError(
    createCollectionPointIsError,
    createCollectionPointError,
    "Create collection point fail"
  );
  useError(
    updateCollectionPointIsError,
    updateCollectionPointError,
    "Update collection point fail"
  );
  useError(
    getSingleCollectionPointIsError,
    getSingleCollectionPointError,
    "Get single collection point data fail"
  );
  useError(getAllAreaIsError, getAllAreaError, "Get all area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");
  useError(
    getAllTerritoryIsError,
    getAllTerritoryError,
    "Get all territory fail"
  );
  useError(getAllRouteIsError, getAllRouteError, "Get all route fail");
  useError(getAllUserIsError, getAllUserError, "Get all users fail");
  useError(
    updateCollectionPointStatusIsError,
    updateCollectionPointStatusError,
    "Collection point status updated fail"
  );

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const showDrawerOfficer = () => {
    setOpenOfficer(true);
  };

  const onCloseOfficer = () => {
    setOpenOfficer(false);
  };

  const getModalTitle = () => {
    if (openConfirmModal?.type === "CONFIRM" && selectedId) {
      return "Collection Point Successfully Update!";
    }
    if (openConfirmModal?.type === "CONFIRM") {
      return "Collection Point Successfully Added!";
    }
    if (
      openConfirmStatusModal?.type === "CONFIRMSTATUS" &&
      openConfirmStatusModal?.data?.active === false
    ) {
      return "Collection Point Successfully Activated!";
    }
    if (
      openConfirmStatusModal?.type === "CONFIRMSTATUS" &&
      openConfirmStatusModal?.data?.active === true
    ) {
      return "Collection Point Successfully Deactivated!";
    }
    return "";
  };

  const handleOkay = () => {
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setSelectedId(null);
    setSelectedZoneId(null);
    setSelectedAreaId(null);
    setSelectedTerritoryId(null);
    form.setFieldsValue({});
    form.resetFields();
    onCloseOfficer();
    handleViewClose();
    handleSuccessClose();
    handleConfirmClose();
    handleConfirmStatusClose();
  };

  const handleStatus = () => {
    const newData = {
      active: openConfirmStatusModal?.data?.active === true ? false : true,
    };
    updateCollectionPointStatus({
      data: newData,
      id: openConfirmStatusModal?.data?.id,
    });
  };

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      organizationPhoto:
        "https://sheba-dev-digigo-bucket.s3.ap-southeast-1.amazonaws.com/upload/1758601667859-empty_user.jpg",
      organizationNameShortHand: values?.organizationName?.toLowerCase(),
      collectionPointType: "individual",
      companyId: user?.data?.company_id,
      active: true,
    };

    handleConfirmOpen({ type: "CONFIRM", data: newData });
  };

  const handleSendToApprove = () => {
    const newUpdateData = {
      ...openConfirmModal?.data,
      active: getSingleCollectionPointData?.data?.active,
    };

    if (openConfirmModal?.type === "CONFIRM" && selectedId) {
      updateCollectionPoint({ data: newUpdateData, id: selectedId });
    } else {
      createCollectionPoint(openConfirmModal?.data);
    }
  };

  useEffect(() => {
    if (getSingleCollectionPointData?.data && selectedId) {
      form.setFieldsValue({
        zoneId: getSingleCollectionPointData.data.zoneId ?? null,
        areaId: getSingleCollectionPointData.data.areaId ?? null,
        territoryId: getSingleCollectionPointData.data.territoryId ?? null,
        routeId: getSingleCollectionPointData.data.routeId ?? null,
        organizationName:
          getSingleCollectionPointData.data.organizationName ?? "",
        address: getSingleCollectionPointData.data.address ?? "",
      });
      setSelectedZoneId(getSingleCollectionPointData.data.zoneId ?? null);
      setSelectedAreaId(getSingleCollectionPointData.data.areaId ?? null);
      setSelectedTerritoryId(
        getSingleCollectionPointData?.data?.territoryId ?? null
      );
    }
  }, [
    getSingleCollectionPointData?.data,
    selectedId,
    form,
    setSelectedZoneId,
    setSelectedAreaId,
    setSelectedTerritoryId,
  ]);

  useEffect(() => {
    if (updateCollectionPointStatusIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCollectionPointStatusIsSuccess]);

  useEffect(() => {
    if (createCollectionPointIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createCollectionPointIsSuccess]);

  useEffect(() => {
    if (updateCollectionPointIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCollectionPointIsSuccess]);

  const viewData = openViewModal?.data;

  const detailsData = [
    {
      label: "Individual Name",
      value: viewData?.organizationName
        ? capitalize(viewData?.organizationName)
        : "N/A",
    },
    // { label: "Contact Number", value: "+880 1234 567890" },
    // { label: "NID", value: "1234567890123" },
    {
      label: "Zone",
      value: viewData?.zoneName ? capitalize(viewData?.zoneName) : "N/A",
    },
    {
      label: "Area",
      value: viewData?.areaName ? capitalize(viewData?.areaName) : "N/A",
    },
    {
      label: "Territory",
      value: viewData?.territoryName
        ? capitalize(viewData?.territoryName)
        : "N/A",
    },
    {
      label: "Route",
      value: viewData?.routeName ? capitalize(viewData?.routeName) : "N/A",
    },
    {
      label: "Status",
      value: viewData?.active === true ? "Active" : "Inactive",
    },
  ];

  const leaveSettingsTabList = [
    {
      label: "Business Points",
      link: `/admin/cash-collection-point`,
    },
    {
      label: "Individual Points",
      link: `/admin/cash-collection-point/individual`,
    },
  ];

  const columns: ColumnsType<GetAllCollectionPointDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap ">Individual Name</div>,
      dataIndex: "organizationName",

      render: (val) => {
        return (
          <>
            <div className="">
              {/* <div className="flex-shrink-0 w-12 h-12">
                <img
                  className="w-full h-full object-cover rounded-full"
                  src={userImage}
                  alt="organization_image"
                />
              </div> */}
              <div className="justify-start text-slate-500 text-sm font-normal">
                {val ? val : "N/A"}
              </div>
            </div>
          </>
        );
      },
    },
    {
      title: <div className="text-nowrap">Address</div>,
      dataIndex: "address",
      align: "center",
      render: (val) => {
        return val || "N/A";
      },
    },

    // {
    //   title: <div className="text-nowrap">Contact Number</div>,
    //   dataIndex: "contactNumber",
    //   align: "center",
    //   render: (val) => {
    //     return val || "N/A";
    //   },
    // },
    // {
    //   title: <div className="text-nowrap">NID</div>,
    //   dataIndex: "nid",
    //   align: "center",
    //   render: (val) => {
    //     return val || "N/A";
    //   },
    // },
    {
      title: <div className="whitespace-nowrap">Status</div>,
      dataIndex: "active",
      align: "center",
      render: (val: boolean) => {
        const status = val ? "Active" : "Inactive";

        const statusClasses: Record<string, string> = {
          Active: "bg-green-100 text-green-700",
          Inactive: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[status]}`}
          >
            {status}
          </span>
        );
      },
    },

    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val: number, record: any) => {
        const items = [
          {
            key: "active",
            label: (
              <div className="flex justify-between items-center pb-2 border-b border-[#D7DFE9]">
                <span className="text-gray-700">Active</span>
                <Switch
                  checked={record?.active === true}
                  size="small"
                  onClick={() => {
                    handleConfirmStatusOpen({
                      type: "CONFIRMSTATUS",
                      data: record,
                    });
                  }}
                />
              </div>
            ),
          },
          {
            key: "details",
            label: (
              <div
                className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                onClick={() => {
                  setSelectedId(val);
                  handleViewOpen({ type: "VIEW", data: record });
                }}
              >
                <EyeOutlined />
                <span>Details</span>
              </div>
            ),
          },
          {
            key: "edit",
            label: (
              <div
                className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                onClick={() => {
                  setSelectedId(val);
                  showDrawerOfficer();
                }}
              >
                <EditIcon />
                <span>Edit</span>
              </div>
            ),
          },
        ];

        return (
          <div className="flex justify-center items-center gap-2">
            <Dropdown
              menu={{
                items,
                className: "min-w-[170px]",
              }}
              trigger={["click"]}
              placement="bottomRight"
              arrow={{ pointAtCenter: true }}
            >
              <Button
                className="action-button disabled:!bg-[#ddd] !border-none"
                size="small"
                icon={<ActionThreeDotIcon />}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="Collection Points" tabList={leaveSettingsTabList}>
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.count}
          dataSource={addKeyInArray(data?.data?.rows)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search by Name"
                style={{ width: 215 }}
                allowClear
                onChange={handleSearch}
              />

              <Select
                placeholder="Status"
                showSearch
                options={[
                  { label: "Active", value: true },
                  {
                    label: "Inactive",
                    value: false,
                  },
                ]}
                value={filter?.active ?? undefined}
                onChange={(value) => handleFilter("active", value)}
                className="!min-w-[110px]"
                suffixIcon={<SelectDownIcon />}
                allowClear
                optionFilterProp="label"
              />

              <IconButton
                className="flex items-center text-[#7a8799]"
                icon={<FilterIconThree />}
                onClick={() => showDrawer()}
              >
                Filter
              </IconButton>

              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
                onClick={() => showDrawerOfficer()}
              >
                Collection Points
              </Button>
            </>
          }
        />
      </SidebarLayout>

      <Drawer open={open} width={400} onClose={onClose}>
        <div className="justify-start text-zinc-700 text-xl font-semibold">
          Filter
        </div>

        <div>
          {/* Zone */}
          <Select
            placeholder="Select Zone"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-8"
            options={
              getAllZoneData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.zoneName),
                value: val?.id,
              })) || []
            }
            value={selectedZoneId}
            onChange={(value) => {
              setSelectedZoneId(value ?? null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
          />

          {/* Area */}
          <Select
            placeholder="Select Area"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedZoneId}
            options={
              getAllAreaData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.areaName),
                value: val?.id,
              })) || []
            }
            value={selectedAreaId}
            onChange={(value) => {
              setSelectedAreaId(value ?? null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
          />

          {/* Territory */}
          <Select
            placeholder="Select Territory"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedAreaId}
            options={
              getAllTerritoryData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.territoryName),
                value: val?.id,
              })) || []
            }
            value={selectedTerritoryId}
            onChange={(value) => {
              setSelectedTerritoryId(value ?? null);
              setSelectedRouteId(null);
            }}
          />

          {/* Route */}
          <Select
            placeholder="Route"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedTerritoryId}
            options={
              getAllRouteData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.routeName),
                value: val?.id,
              })) || []
            }
            value={selectedRouteId}
            onChange={(value) => setSelectedRouteId(value ?? null)}
          />

          {/* Field Officer */}
          {/* <Select
            placeholder="Field Officer"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            options={
              getAllUserData?.data?.users?.map((user: any) => ({
                label: capitalize(user.username),
                value: user.id,
              })) || []
            }
            value={selectedUserId}
            onChange={(value) => setSelectedUserId(value ?? null)}
          /> */}
        </div>

        <Space className="w-full mt-8" size={12}>
          {/* Cancel → clear everything */}
          <Button
            className="normal-dark-button modal-button !h-[40px]"
            onClick={() => {
              setSelectedZoneId(null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
              setSelectedUserId(null);
              handleFilter("zoneId", undefined);
              handleFilter("areaId", undefined);
              handleFilter("territoryId", undefined);
              handleFilter("routeId", undefined);
              // handleFilter("userId", undefined);
              onClose();
            }}
            size="middle"
          >
            Cancel
          </Button>

          {/* Apply Filter */}
          <Button
            className="modal-button !h-[40px]"
            type="primary"
            size="middle"
            onClick={() => {
              handleFilter("zoneId", selectedZoneId ?? undefined);
              handleFilter("areaId", selectedAreaId ?? undefined);
              handleFilter("territoryId", selectedTerritoryId ?? undefined);
              handleFilter("routeId", selectedRouteId ?? undefined);
              // handleFilter("userId", selectedUserId ?? undefined);
              onClose();
            }}
          >
            Filter
          </Button>
        </Space>
      </Drawer>

      <Drawer open={openOfficer} width={400} onClose={handleCloseModal}>
        <div className="justify-start text-zinc-700 text-xl font-semibold">
          {selectedId ? "Update Collection Point" : "Add New Collection Point"}
        </div>
        {getSingleCollectionPointIsLoading ||
        getSingleCollectionPointIsFetching ||
        getAllZoneIsLoading ||
        getAllZoneIsFetching ||
        getAllAreaIsLoading ||
        getAllAreaIsFetching ||
        getAllTerritoryIsLoading ||
        getAllTerritoryIsFetching ||
        getAllRouteIsLoading ||
        getAllRouteIsFetching ? (
          <Loader />
        ) : (
          <div className="mt-5">
            <Form
              className="custom-form"
              layout="vertical"
              requiredMark="optional"
              size="large"
              onFinish={onFinish}
              form={form}
            >
              <Form.Item
                label="Zone"
                name="zoneId"
                rules={[
                  {
                    required: true,
                    message: "Please enter zone",
                  },
                ]}
              >
                <Select
                  placeholder="Select Zone"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  options={
                    getAllZoneData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.zoneName),
                      value: val?.id,
                    })) || []
                  }
                  onChange={(value) => setSelectedZoneId(value)}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Area"
                name="areaId"
                rules={[
                  {
                    required: true,
                    message: "Please enter area",
                  },
                ]}
              >
                <Select
                  placeholder="Select Area"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  disabled={!selectedZoneId}
                  options={
                    getAllAreaData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.areaName),
                      value: val?.id,
                    })) || []
                  }
                  onChange={(value) => setSelectedAreaId(value)}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Teritory"
                name="territoryId"
                rules={[
                  {
                    required: true,
                    message: "Please enter teritory",
                  },
                ]}
              >
                <Select
                  placeholder="Select Territory"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  disabled={!selectedAreaId}
                  options={
                    getAllTerritoryData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.territoryName),
                      value: val?.id,
                    })) || []
                  }
                  onChange={(value) => setSelectedTerritoryId(value)}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Route"
                name="routeId"
                rules={[
                  {
                    required: true,
                    message: "Please enter route",
                  },
                ]}
              >
                <Select
                  placeholder="Route"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  disabled={!selectedTerritoryId}
                  options={
                    getAllRouteData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.routeName),
                      value: val?.id,
                    })) || []
                  }
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Collection Point"
                name="organizationName"
                rules={[
                  {
                    required: true,
                    message: "Please enter collection point",
                  },
                ]}
              >
                <Input placeholder="Shop Name" />
              </Form.Item>

              <Form.Item
                label="Full Address"
                name="address"
                rules={[
                  {
                    required: true,
                    message: "Please enter full address",
                  },
                ]}
              >
                <Input placeholder="Full Address" />
              </Form.Item>

              {/* <Form.Item
              label="Field Officer"
              name="userId"
              rules={[
                {
                  required: true,
                  message: "Please enter field officer",
                },
              ]}
            >
              <Select
                placeholder="Select Field Officer"
                suffixIcon={<SelectDownIcon />}
                showSearch
                allowClear
                className="w-full"
                options={
                  getAllUserData?.data?.users?.map((user: any) => ({
                    label: capitalize(user.username),
                    value: user.id,
                  })) || []
                }
                optionFilterProp="label"
              />
            </Form.Item> */}

              <Space className="w-full mt-5" size={12}>
                <Button
                  className="normal-dark-button modal-button !h-[40px]"
                  onClick={handleCloseModal}
                  size="middle"
                >
                  Cancel
                </Button>

                <Button
                  className="modal-button !h-[40px]"
                  type="primary"
                  size="middle"
                  htmlType="submit"
                >
                  {selectedId
                    ? "Update Collection Point"
                    : "Add Collection Point"}
                </Button>
              </Space>
            </Form>
          </div>
        )}
      </Drawer>

      {openViewModal?.type === "VIEW" && (
        <CashCollectionDetailsModal
          open={openViewModal?.open}
          handleClose={handleViewClose}
        >
          <div>
            {/* <div className="flex justify-start items-center gap-3">
              <div className="!w-16 !h-16 rounded-full">
                <img
                  className="w-full h-full"
                  src={userImage}
                  alt="details_image"
                />
              </div>
              <div>
                <div className="justify-start text-zinc-700 text-lg font-medium">
                  Birchwood Market
                </div>
                <div className="flex justify-start items-center gap-1 text-slate-500 text-sm font-normal">
                  <span>
                    <LoactionIcon />
                  </span>
                  <span>123 Gulshan Avenue, Dhaka, Bangladesh</span>
                </div>
              </div>
            </div> */}
            <div className="justify-start text-zinc-700 text-lg font-medium">
              Details
            </div>

            <div className="pr-4 mt-5">
              {detailsData.map((item, index) => (
                <div
                  key={index}
                  className={`flex justify-between py-2 ${
                    index !== detailsData.length - 1
                      ? "border-b border-[#D7DFE9]"
                      : ""
                  }`}
                >
                  <span className="text-slate-500 text-base font-normal">
                    {item.label}
                  </span>
                  <span
                    className={`text-base font-normal px-2 py-1 rounded ${
                      item.value === "Active"
                        ? "bg-green-100 text-green-700"
                        : item.value === "Inactive"
                        ? "bg-red-100 text-red-700"
                        : "text-zinc-700"
                    }`}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CashCollectionDetailsModal>
      )}

      {openConfirmModal?.type === "CONFIRM" && (
        <ConfirmModalLayout
          title={`${
            selectedId
              ? "Update Collection Point?"
              : "Add New Collection Point?"
          }`}
          subTitle={`${
            selectedId
              ? "Are you sure you want to Update collection point?"
              : "Are you sure you want to add new collection point?"
          }`}
          buttonName={`${
            selectedId ? "Update Collection Point" : "Add Collection Point"
          }`}
          handleButton={handleSendToApprove}
          open={openConfirmModal?.open}
          handleClose={handleConfirmClose}
          action
          buttonBgDeep
          loading={
            createCollectionPointIsLoading || updateCollectionPointIsLoading
          }
        ></ConfirmModalLayout>
      )}

      {openConfirmStatusModal?.type === "CONFIRMSTATUS" && (
        <ConfirmModalLayout
          title={`${
            openConfirmStatusModal?.data?.active === true
              ? "Inactivate Collection Point?"
              : "Activate Collection Point?"
          }`}
          subTitle={`${
            openConfirmStatusModal?.data?.active === true
              ? "Are you sure you want to inactivate this Collection Point?"
              : "Are you sure you want to activate this Collection Point?"
          }`}
          buttonName={`${
            openConfirmStatusModal?.data?.active === true
              ? "Inactive"
              : "Active"
          }`}
          handleButton={handleStatus}
          open={openConfirmStatusModal?.open}
          handleClose={handleConfirmStatusClose}
          action
          buttonBgDeep
          loading={updateCollectionPointStatusIsLoading}
        ></ConfirmModalLayout>
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={getModalTitle()}
        />
      )}
    </>
  );
}
